// One continuous sit-down at a live table: the roster, the carried stacks, the button, the reads.
// TB0_CONTINUOUS_TABLE_SPEC.md §2 (the state shape) + §7 (the persisted snapshot).
//
// Plain serializable data by design - no class, no live engine objects held across hands. TableSession
// IS the snapshot payload (§7.1), so it drops into localStorage (TB2) or a D1 `sessions` row later
// without a reshape. It holds NO hands: `seedRoot` + `handNumber` re-derive any hand deterministically
// (§4.4), which is what keeps the snapshot under ~2KB.
import { z } from 'zod';
import { ArchetypeIdSchema } from './archetype';
import { TableTypeSchema } from './read';
import { StakesSchema } from './scenario';
/** How you sat down. A PRESENTATION overlay (GH4): 'new' paints every seat '?' for a cold read,
 *  'mid' shows the diagnosed ring. The dealt hand is identical either way. */
export const TableModeSchema = z.enum(['new', 'mid']);
/** The compounding read on a seat - it lives on the SESSION, not the hand (§6.3). Tag seat 4 as a
 *  Whale on hand 3 and on hand 9 that tag is still there, because it is the same guy. */
export const SeatReadSchema = z.object({
    /** false ⇒ the pod shows '?' (the GH4 cold read). */
    revealed: z.boolean(),
    /** the Axis-B three-tag read; up to 3, PERSISTS across hands. */
    userTags: z.array(ArchetypeIdSchema).max(3).optional(),
    confirmedTag: ArchetypeIdSchema.optional(),
});
export const TableSeatSchema = z.object({
    /** 0..n-1. Seats are DENSE - there is no sitting-out concept in createHand (§5). */
    seat: z.number().int().nonnegative(),
    kind: z.enum(['hero', 'villain']),
    /** villains only; FIXED for the session - the reads compounding is the whole point. */
    archetypeId: ArchetypeIdSchema.optional(),
    displayName: z.string().min(1),
    /** CARRIED chips. The load-bearing field: hand N's ending stack IS hand N+1's starting stack. */
    stack: z.number().nonnegative(),
    read: SeatReadSchema,
});
/** Every chip INJECTION, so the across-session ledger identity stays honest rather than a silent
 *  leak (§5.2). Chips are never created anywhere else. */
export const SessionEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('buyin'),
        seat: z.number().int().nonnegative(),
        amount: z.number().positive(),
        handNumber: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('reload'),
        seat: z.number().int().nonnegative(),
        amount: z.number().positive(),
        handNumber: z.number().int().nonnegative(),
    }),
]);
export const TableSessionSchema = z.object({
    sessionId: z.string().min(1),
    stakes: StakesSchema,
    tableType: TableTypeSchema,
    /** index === seat number, hero at 0. SIZE-AGNOSTIC: `seats.length` is the truth, not tableSize. */
    seats: z.array(TableSeatSchema).min(2).max(9),
    /** AUTHORITATIVE (§4.2). Hero's position is DERIVED from it, never pinned - that inversion is
     *  what kept hero stuck on the button forever. */
    buttonSeat: z.number().int().nonnegative(),
    /** 1-based; the hand about to be dealt. */
    handNumber: z.number().int().positive(),
    /** hand N's seed is `${seedRoot}::h${handNumber}` (§4.4) - the same root replays the identical
     *  session, hand for hand. */
    seedRoot: z.string().min(1),
    tableMode: TableModeSchema,
    /** the seed/reload amount. $300 by default; ARBITRARY, uncapped (§4.3). */
    buyIn: z.number().positive(),
    ledger: z.array(SessionEventSchema),
});
/** Bump whenever TableSession changes shape; old saves then self-evict (§7.3, discard-don't-migrate). */
export const SESSION_SNAPSHOT_VERSION = 1;
/** The persisted snapshot. TableSession IS the payload, by design (§7.1). */
export const SessionSnapshotSchema = z.object({
    v: z.literal(SESSION_SNAPSHOT_VERSION),
    /** epoch ms, for the resume copy ("23 hands in, 2 hours ago"). */
    savedAt: z.number().int().nonnegative(),
    session: TableSessionSchema,
});
//# sourceMappingURL=session.js.map